import type { ReactNode } from "react";
import type { FilterState } from "@/lib/movies";

interface FilterSidebarProps {
  filter: FilterState;
  genreOptions: string[];
  regionOptions: string[];
  yearOptions: number[];
  onToggle: (group: "genres" | "regions" | "years", value: string | number) => void;
  onSort: (sort: "latest" | "hot") => void;
  onReset: () => void;
}

function Group({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="py-4 border-b border-background-200 last:border-b-0">
      <h3 className="text-sm font-semibold text-foreground-900 mb-3">{title}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterSidebar({
  filter,
  genreOptions,
  regionOptions,
  yearOptions,
  onToggle,
  onSort,
  onReset,
}: FilterSidebarProps) {
  const chipCls = (active: boolean) =>
    `px-3 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
      active
        ? "bg-primary-500 text-background-50 font-medium"
        : "bg-background-100 text-foreground-600 hover:bg-background-200"
    }`;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
      <div className="flex items-center justify-between py-4 border-b border-background-200">
        <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-500" />
          筛选与排序
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer"
        >
          重置
        </button>
      </div>

      <Group title="排序">
        {(
          [
            { key: "latest", label: "最新" },
            { key: "hot", label: "最热" },
          ] as const
        ).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onSort(item.key)}
            className={chipCls(filter.sort === item.key)}
          >
            {item.label}
          </button>
        ))}
      </Group>

      <Group title="类型">
        {genreOptions.map((genre) => (
          <button
            key={genre}
            type="button"
            onClick={() => onToggle("genres", genre)}
            className={chipCls(filter.genres.includes(genre))}
          >
            {genre}
          </button>
        ))}
      </Group>

      <Group title="地区">
        {regionOptions.map((region) => (
          <button
            key={region}
            type="button"
            onClick={() => onToggle("regions", region)}
            className={chipCls(filter.regions.includes(region))}
          >
            {region}
          </button>
        ))}
      </Group>

      <Group title="年份">
        {yearOptions.map((year) => (
          <button
            key={year}
            type="button"
            onClick={() => onToggle("years", year)}
            className={chipCls(filter.years.includes(year))}
          >
            {year}
          </button>
        ))}
      </Group>
    </div>
  );
}